import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { apiJson, ApiError } from '../lib/api'
import { PairTabs, sitesTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { useLayer, useChanged } from '../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../components/pending'
import { plural } from './wages/format'
import {
  HistoryEvent,
  MAX_ADD,
  SiteVariety,
  Structure,
  StructureBoundary,
  StructureJob,
  StructureNode,
  StructureSite,
  activeRowsUnder,
  afterArchive,
  afterMove,
  areaText,
  boundariesOnArchive,
  boundariesOnMove,
  childrenOf,
  common,
  coverageOf,
  emptiedBy,
  generatedNames,
  historyActor,
  historyEntry,
  isActiveRow,
  isUnder,
  kg,
  leavesNoActiveRow,
  mappingCell,
  parentPath,
  pathOf,
  pinOf,
  previewLine,
  shownPath,
  suggestedStart,
  varietyPlan,
} from './sites/structure'
import { fromSelectionBlock } from './pickingJobs/members'
import { ImportDialog } from './sites/ImportDialog'
import { AddSiteForm, SiteMenu, SiteSettingsDialog, SiteSummary, VarietySelect, addedToast, hectares } from './sites/SiteMenu'
import sm from './sites/SiteMenu.module.css'
import pa from './ProductActivityPage.module.css'
import pj from './PickingJobsPage.module.css'
import o from '../components/Overlay.module.css'
import styles from './SitesPage.module.css'
import { openableRow } from '../lib/openableRow'

// Structure (handoff §7g, "The hierarchy and its rules" and "Structure view").
// A compact navigator on the left, the dense row table beside it, a row
// inspector slide-over, and the contextual toolbar: Edit selected · Move ·
// Create job from selection · Replant · Archive · Clear. Create job opens
// Picking jobs on a new job with the selection (?rows=); it asks for one
// unassigned selection of one variety and says why when it is not. Every write is the server's to decide
// (routes/structure.js); the previews here read the structure the same way
// so a refusal is rarely a surprise, and it is shown when it is one.
//
// The mapping column reads the Site's live boundaries (084): Mapped /
// Unmapped at row mapping, the ancestor's coverage below it. Move and
// Archive count the boundaries they remove and send that count, so the
// server refuses when the map changed after the preview was drawn.
//
// Reorder lists a level 1's or level 2's siblings to move a place at a time
// (POST .../structure/reorder); a level 1's Move says it can only be
// reordered and opens it. Rows are listed by name.
//
// Not here, deliberately: drawing boundaries (the Map view, SitesMapPage) and
// restoring an archived row.

type Dialog =
  | { kind: 'add'; start?: 'rows' | 'l2' | 'l1' }
  | { kind: 'bulk' }
  | { kind: 'move'; ids: string[] }
  | { kind: 'archive'; rowIds?: string[]; nodeId?: string }
  | { kind: 'replant'; id: string }
  | { kind: 'rename'; id: string }
  | { kind: 'reorder'; parentId: string | null; focusId?: string }
  | { kind: 'history' }
  | { kind: 'settings' }
  | { kind: 'import' }

const lower = (s: string) => s.toLowerCase()
const err = (e: any, fallback: string) => (e?.message as string) || fallback

export const SitesPage: React.FC = () => {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const [sites, setSites] = useState<SiteSummary[] | null>(null)
  const [data, setData] = useState<Structure | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [focus, setFocus] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [unassignedOnly, setUnassignedOnly] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [inspect, setInspect] = useState<string | null>(null)
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [toast, setToast] = useState('')

  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid

  // The list of Sites could not be read: not the same as there being none, so no "create your first site" is offered over it.
  const [sitesFailed, setSitesFailed] = useState(false)
  const loadSites = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    setError(null)
    try {
      setSites(await apiJson<SiteSummary[]>(u, '/api/sites'))
      setSitesFailed(false)
    } catch (e: any) {
      setError(err(e, 'Could not load the sites'))
      setSites((s) => s || [])
      setSitesFailed(true)
    }
  }, [uid])

  useEffect(() => {
    loadSites()
  }, [loadSites])

  // The Site in view is shared with Picking jobs through ?site= (§7g, "one
  // shared Site selector").
  const siteId = params.get('site') || sites?.find((s) => !s.archived_at)?.id || sites?.[0]?.id || ''

  // A late answer for the Site left behind must not replace the Site now in view.
  const loadSeq = useRef(0)
  const load = useCallback(async () => {
    const u = userRef.current
    if (!u || !siteId) return
    const n = ++loadSeq.current
    try {
      const d = await apiJson<Structure>(u, `/api/sites/${siteId}/structure`)
      if (n !== loadSeq.current) return
      setData(d)
      // A selection names only rows that are still active.
      const live = new Set(d.nodes.filter(isActiveRow).map((n) => n.id))
      setSelected((s) => new Set([...s].filter((id) => live.has(id))))
      setError(null)
    } catch (e: any) {
      if (n !== loadSeq.current) return
      setError(err(e, 'Could not load the structure'))
    }
  }, [uid, siteId])

  useEffect(() => {
    setData(null)
    setFocus(null)
    setSelected(new Set())
    setInspect(null)
    setDialog(null)
    load()
  }, [load])

  // Global search links here (h 465-485) with ?q=, the location's name, to
  // fill the structure's search box. ?site= stays; ?q= is dropped.
  const linkedQ = params.get('q')
  useEffect(() => {
    if (linkedQ === null) return
    setQ(linkedQ)
    const next = new URLSearchParams(params)
    next.delete('q')
    setParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedQ])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 4200)
    return () => clearTimeout(t)
  }, [toast])

  const nodes = data?.nodes || []
  const site = data?.site || null
  const jobs = data?.jobs || []
  const boundaries = data?.boundaries || []
  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes])
  const focusNode = focus ? byId.get(focus) || null : null

  // Tree order, so the table reads in the same order as the navigator.
  const rank = useMemo(() => {
    const out = new Map<string, number>()
    let i = 0
    const walk = (parent: string | null) => {
      for (const n of childrenOf(nodes, parent)) {
        out.set(n.id, i++)
        if (n.level < 3) walk(n.id)
      }
    }
    walk(null)
    return out
  }, [nodes])

  const tableRows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return nodes
      .filter((n) => n.level === 3)
      .filter((n) => showArchived || !n.archived_at)
      .filter((n) => !focus || isUnder(byId, n, focus))
      .filter((n) => !unassignedOnly || (!n.archived_at && !n.picking_job_id))
      .filter((n) => !needle || [n.name, pathOf(byId, n), n.location_id || ''].some((v) => v.toLowerCase().includes(needle)))
      .sort((a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0))
  }, [nodes, byId, rank, focus, q, unassignedOnly, showArchived])

  // The rows in view before the search and "Unassigned only" narrow them:
  // the "of" in the count under the table.
  const scopeRows = useMemo(
    () => nodes.filter((n) => n.level === 3 && (showArchived || !n.archived_at) && (!focus || isUnder(byId, n, focus))),
    [nodes, byId, focus, showArchived]
  )
  const selectable = tableRows.filter(isActiveRow)
  const allOn = selectable.length > 0 && selectable.every((n) => selected.has(n.id))
  const selRows = [...selected].map((id) => byId.get(id)).filter((n): n is StructureNode => !!n && isActiveRow(n))
  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const done = async (message: string) => {
    setDialog(null)
    setToast(message)
    await load()
  }

  if (!sites && !error) {
    return (
      <div className={styles.container}>
        <h1>Sites & jobs</h1>
        <div className={styles.loading}>Loading sites...</div>
      </div>
    )
  }

  const labels = site ? [site.level1_label, site.level2_label, site.level3_label] : ['Level 1', 'Level 2', 'Row']
  // "Hide level 2 in the interface": two levels are shown, three are held.
  const hideL2 = site?.show_level2 === false
  const archivedSite = !!site?.archived_at
  const siteAdded = async (s: { id: string; name: string }) => {
    await loadSites()
    setParams({ site: s.id }, { replace: true })
    setToast(addedToast(s.name))
  }
  const inspected = inspect ? byId.get(inspect) || null : null

  return (
    <>
      <PageHeader
        title="Sites & jobs"
        tabs={<PairTabs label="Sites and jobs" tabs={sitesTabs(siteId)} current="Structure" />}
        actions={
          <>
            <button type="button" className={headerStyles.btn} disabled={!site} onClick={() => setDialog({ kind: 'history' })}>
              <svg className={styles.btnIcon} viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              History
            </button>
            <button
              type="button"
              className={headerStyles.btn}
              disabled={!site || archivedSite}
              title={archivedSite ? 'This site is archived and takes no new rows' : undefined}
              onClick={() => setDialog({ kind: 'import' })}
            >
              <svg className={styles.btnIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="M7 10l5 5 5-5M12 15V3" />
              </svg>
              Import
            </button>
          </>
        }
      >
        {sites && sites.length > 0 && (
          <SiteMenu
            user={user}
            sites={sites}
            siteId={siteId}
            onPick={(id) => setParams({ site: id }, { replace: true })}
            onAdded={siteAdded}
            onSettings={() => setDialog({ kind: 'settings' })}
            onVarietiesChanged={load}
          />
        )}
      </PageHeader>

      {error && (
        <div className={styles.error} role="alert">
          <p>{error}</p>
          <button onClick={() => (sitesFailed ? loadSites() : load())}>Retry</button>
        </div>
      )}

      {sitesFailed && !sites?.length ? null : sites && sites.length === 0 ? (
        <PageBody>
          <div className={styles.empty}>
            <p>No sites configured yet</p>
            <p className={styles.emptyHint}>Create your first site to start managing structure and picking jobs</p>
            <AddSiteForm user={user} className={sm.emptyAdd} onAdded={siteAdded} />
          </div>
        </PageBody>
      ) : (
        <PageBody className={styles.body}>
          {!data ? (
            !error && <div className={styles.loading}>Loading the structure...</div>
          ) : (
            <div className={styles.layout}>
              <Navigator
                nodes={nodes}
                site={data.site}
                focus={focus}
                showArchived={showArchived}
                hideL2={hideL2}
                archivedSite={archivedSite}
                // Picking the node in view again lets it go (V4).
                onFocus={(id) => setFocus((f) => (f === id ? null : id))}
                onAdd={(start) => setDialog({ kind: 'add', start })}
              />

              <div className={styles.card}>
                <div className={styles.scopeHead}>
                  <div className={styles.scopeText}>
                    <div className={styles.scopeKind}>{focusNode ? labels[focusNode.level - 1] : 'Site'}</div>
                    <div className={styles.scopeTitle}>{focusNode ? shownPath(byId, focusNode, hideL2) : `All of ${data.site.name}`}</div>
                    {!focusNode &&
                      (() => {
                        // The site's address, size and timezone. V4 has no
                        // place for them on this view, so they sit under the
                        // site's name rather than being dropped.
                        const s = (sites || []).find((x) => x.id === siteId)
                        const ha = s ? hectares(s.size_hectares) : null
                        const bits = s ? [s.address, ha ? `${ha} ha` : '', s.timezone, s.archived_at ? 'Archived' : ''].filter(Boolean) : []
                        return bits.length ? (
                          <div className={styles.siteMeta} title={bits.join(' · ')}>
                            {bits.join(' · ')}
                          </div>
                        ) : null
                      })()}
                    {focusNode && !focusNode.active && <div className={styles.siteMeta}>Empty: kept for history</div>}
                  </div>
                  {focusNode && focusNode.level < 3 && (
                    <div className={styles.scopePin}>
                      <PinStrip
                        key={focusNode.id}
                        user={user}
                        node={focusNode}
                        nodes={nodes}
                        site={data.site}
                        onSaved={(m) => {
                          setToast(m)
                          load()
                        }}
                      />
                    </div>
                  )}
                  {focusNode && (
                    <div className={styles.scopeBtns}>
                      <button type="button" className={styles.ghostBtn} onClick={() => setFocus(null)}>
                        Show all
                      </button>
                      {focusNode.level < 3 && (
                        <>
                          <button type="button" className={styles.ghostBtn} onClick={() => setDialog({ kind: 'rename', id: focusNode.id })}>
                            Rename
                          </button>
                          <button type="button" className={styles.ghostBtn} onClick={() => setDialog({ kind: 'move', ids: [focusNode.id] })}>
                            Move
                          </button>
                          <button
                            type="button"
                            className={styles.ghostBtn}
                            onClick={() => setDialog({ kind: 'reorder', parentId: focusNode.parent_id || null, focusId: focusNode.id })}
                          >
                            Reorder
                          </button>
                          <button
                            type="button"
                            className={styles.ghostBtn}
                            disabled={!focusNode.active}
                            onClick={() => setDialog({ kind: 'archive', nodeId: focusNode.id })}
                          >
                            Archive branch
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  <div className={styles.scopeTools}>
                    <input className={styles.search} placeholder="Name, path or Location ID" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search rows" />
                    <button type="button" aria-pressed={unassignedOnly} className={unassignedOnly ? styles.flagOn : styles.flag} onClick={() => setUnassignedOnly(!unassignedOnly)}>
                      Unassigned only
                    </button>
                    <button type="button" aria-pressed={showArchived} className={showArchived ? styles.flagOn : styles.flag} onClick={() => setShowArchived(!showArchived)}>
                      Show archived
                    </button>
                    <button
                      type="button"
                      className={styles.addBtn}
                      disabled={archivedSite}
                      title={
                        archivedSite
                          ? 'This site is archived and takes no new rows'
                          : focusNode && focusNode.level < 3
                            ? `Into ${shownPath(byId, focusNode, hideL2)}`
                            : undefined
                      }
                      onClick={() => setDialog({ kind: 'add' })}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      Add {lower(labels[2])}s
                    </button>
                  </div>
                </div>

                <div className={styles.table} role="table" aria-label="Rows">
                  <div className={styles.headRow} role="row">
                    <span role="columnheader">
                      <input
                        type="checkbox"
                        className={styles.check}
                        aria-label="Select all shown"
                        checked={allOn}
                        disabled={!selectable.length}
                        onChange={() =>
                          setSelected((s) => {
                            const next = new Set(s)
                            for (const n of selectable) allOn ? next.delete(n.id) : next.add(n.id)
                            return next
                          })
                        }
                      />
                    </span>
                    {[labels[2], 'Product / variety', 'Plants', 'Location ID', 'Picking job', 'Mapping'].map((h) => (
                      <span key={h} role="columnheader">
                        {h}
                      </span>
                    ))}
                  </div>
                  <div className={styles.rows} role="rowgroup">
                    {tableRows.length === 0 && (
                      <div className={styles.emptyRows}>
                        {q.trim() || unassignedOnly
                          ? 'No row matches.'
                          : nodes.some((n) => n.level === 3)
                            ? 'No rows here.'
                            : `This Site has no structure yet. Add ${lower(labels[0])} creates a complete path.`}
                      </div>
                    )}
                    {tableRows.map((n) => (
                      <RowLine
                        key={n.id}
                        n={n}
                        path={n.parent_id ? shownPath(byId, byId.get(n.parent_id), hideL2) : ''}
                        job={jobs.find((j) => j.id === n.picking_job_id) || null}
                        mapping={site && !n.archived_at ? mappingCell(byId, site, boundaries, n) : null}
                        on={selected.has(n.id)}
                        onToggle={() => toggle(n.id)}
                        onOpen={() => setInspect(n.id)}
                      />
                    ))}
                  </div>
                </div>
                <div className={styles.foot}>
                  {tableRows.length} of {plural(scopeRows.length, lower(labels[2]), `${lower(labels[2])}s`)}
                  {unassignedOnly ? ' · unassigned only' : ''}
                  {showArchived ? ' · including archived' : ''}
                </div>

                {selRows.length > 0 && (
                  <div className={styles.selBar} role="toolbar" aria-label="Selected rows">
                    <span className={styles.selLabel}>{plural(selRows.length, 'selected', 'selected')}</span>
                    <div className={styles.selBtns}>
                      <button type="button" className={styles.selBtn} onClick={() => setDialog({ kind: 'bulk' })}>
                        Edit selected
                      </button>
                      <button type="button" className={styles.selBtn} onClick={() => setDialog({ kind: 'move', ids: selRows.map((r) => r.id) })}>
                        Move
                      </button>
                      {(() => {
                        // "Mixed varieties must be resolved first — never silently combine them" (§7g).
                        const why = archivedSite ? 'This site is archived and takes no new jobs' : fromSelectionBlock(selRows)
                        return (
                          <button
                            type="button"
                            className={why ? styles.selBtn : styles.selBtnGo}
                            disabled={!!why}
                            title={why || undefined}
                            onClick={() => navigate(`/picking-jobs?site=${siteId}&rows=${selRows.map((r) => r.id).join(',')}`)}
                          >
                            Create job from selection
                          </button>
                        )
                      })()}
                      <button
                        type="button"
                        className={styles.selBtn}
                        disabled={selRows.length !== 1 || archivedSite}
                        title={selRows.length !== 1 ? `Replant works on one ${lower(labels[2])}` : undefined}
                        onClick={() => setDialog({ kind: 'replant', id: selRows[0].id })}
                      >
                        Replant
                      </button>
                      <button type="button" className={styles.selBtnDanger} onClick={() => setDialog({ kind: 'archive', rowIds: selRows.map((r) => r.id) })}>
                        Archive
                      </button>
                      <button type="button" className={styles.selClear} onClick={() => setSelected(new Set())}>
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </PageBody>
      )}

      {inspected && data && (
        <Inspector
          key={inspected.id}
          user={user}
          row={inspected}
          nodes={nodes}
          site={data.site}
          jobs={jobs}
          varieties={data.varieties}
          onClose={() => setInspect(null)}
          onSaved={(m) => {
            setToast(m)
            load()
          }}
          onReplant={() => setDialog({ kind: 'replant', id: inspected.id })}
          onArchive={() => setDialog({ kind: 'archive', rowIds: [inspected.id] })}
        />
      )}

      {dialog && data && dialog.kind === 'add' && <AddMany user={user} data={data} focus={focusNode} start={dialog.start} onClose={() => setDialog(null)} onDone={done} />}
      {dialog && data && dialog.kind === 'bulk' && <BulkEdit user={user} data={data} rows={selRows} onClose={() => setDialog(null)} onDone={done} onStale={load} />}
      {dialog && data && dialog.kind === 'move' && (
        <MoveDialog
          user={user}
          data={data}
          ids={dialog.ids}
          onClose={() => setDialog(null)}
          onDone={done}
          onStale={load}
          onReorder={(n) => setDialog({ kind: 'reorder', parentId: n.parent_id || null, focusId: n.id })}
        />
      )}
      {dialog && data && dialog.kind === 'reorder' && (
        <ReorderDialog user={user} data={data} parentId={dialog.parentId} focusId={dialog.focusId} onClose={() => setDialog(null)} onDone={done} onStale={load} />
      )}
      {dialog && data && dialog.kind === 'archive' && (
        <ArchiveDialog
          user={user}
          data={data}
          rowIds={dialog.rowIds}
          nodeId={dialog.nodeId}
          onClose={() => setDialog(null)}
          onReplant={(id) => setDialog({ kind: 'replant', id })}
          onStale={load}
          onDone={(m) => {
            setInspect(null)
            setSelected(new Set())
            return done(m)
          }}
        />
      )}
      {dialog && data && dialog.kind === 'replant' && byId.get(dialog.id) && (
        <ReplantDialog
          user={user}
          data={data}
          row={byId.get(dialog.id)!}
          onClose={() => setDialog(null)}
          onDone={(m) => {
            setInspect(null)
            setSelected(new Set())
            return done(m)
          }}
        />
      )}
      {dialog && data && dialog.kind === 'rename' && byId.get(dialog.id) && <RenameDialog user={user} data={data} node={byId.get(dialog.id)!} onClose={() => setDialog(null)} onDone={done} />}
      {dialog && data && dialog.kind === 'history' && <HistoryPanel user={user} data={data} onClose={() => setDialog(null)} />}
      {dialog && data && dialog.kind === 'import' && <ImportDialog user={user} data={data} onClose={() => setDialog(null)} onDone={done} />}
      {dialog && dialog.kind === 'settings' && siteId && (
        <SiteSettingsDialog
          user={user}
          siteId={siteId}
          onClose={() => setDialog(null)}
          onSaved={async (_s, m) => {
            setDialog(null)
            setToast(m)
            await loadSites()
            await load()
          }}
        />
      )}

      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </>
  )
}

export function Padlock() {
  return (
    <svg className={styles.lock} viewBox="0 0 12 12" aria-hidden="true">
      <rect x="2" y="5.2" width="8" height="5.8" rx="1.3" fill="currentColor" />
      <path d="M3.9 5.4V3.9a2.1 2.1 0 0 1 4.2 0v1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  )
}

// The picking-job cell: Unassigned, Assigned, or permanent once picking is
// recorded (§7g, "picking recorded → permanent"). The job is a pill in a soft
// tint of its colour with a dot in the colour itself, since colour is the
// job's identity here (§7g); the tint keeps any colour legible in either
// theme.
function JobCell({ n, job }: { n: StructureNode; job: StructureJob | null }) {
  if (!job)
    return (
      <span className={styles.jobCell}>
        <span className={styles.jobNone}>
          <span className={styles.jobDot} aria-hidden="true" />
          <span className={styles.jobText}>Unassigned</span>
        </span>
      </span>
    )
  return (
    <span className={styles.jobCell}>
      <span
        className={job.colour ? styles.jobBadge : styles.jobBadgeNone}
        style={job.colour ? ({ '--job': job.colour } as React.CSSProperties) : undefined}
        data-testid="job-badge"
        title={job.name}
      >
        <span className={styles.jobDot} data-testid="job-dot" aria-hidden="true" />
        <span className={styles.jobText}>{job.name}</span>
      </span>
      {n.picking_recorded ? (
        <span className={styles.permanent} title="Picking is recorded against this row, so it stays in this job">
          <Padlock /> {kg(n.picked_kg)} · permanent
        </span>
      ) : (
        <span className={styles.assigned}>Assigned</span>
      )}
    </span>
  )
}

function RowLine({
  n,
  path,
  job,
  mapping,
  on,
  onToggle,
  onOpen,
}: {
  n: StructureNode
  path: string
  job: StructureJob | null
  mapping: { text: string; mapped: boolean } | null
  on: boolean
  onToggle: () => void
  onOpen: () => void
}) {
  const archived = !!n.archived_at
  return (
    <div role="row" data-testid="structure-row" className={archived ? styles.rowOff : on ? styles.rowOn : styles.row} {...openableRow(onOpen)}>
      <span onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" className={styles.check} aria-label={`Select ${n.name}`} checked={on} disabled={archived} onChange={onToggle} />
      </span>
      <div className={styles.nameCell}>
        <div className={styles.rowName}>
          <span className={styles.ellipsis}>{n.name}</span>
          {archived && <span className={styles.archivedTag}>Archived</span>}
        </div>
        <div className={styles.rowPath}>{path}</div>
      </div>
      <div className={styles.nameCell}>
        <div className={n.exact_variety ? styles.variety : styles.varietyNone} title={n.exact_variety || undefined}>
          {n.exact_variety || 'No variety'}
        </div>
        {n.picking_recorded && (
          <div className={styles.locked} title="Picking is recorded against this row. Replant it to change what is planted.">
            Variety locked
          </div>
        )}
      </div>
      <span className={n.plant_count == null ? styles.cellNone : styles.num}>{n.plant_count == null ? '—' : n.plant_count.toLocaleString('en-NZ')}</span>
      <span className={n.location_id ? styles.locId : styles.cellNone}>{n.location_id || '—'}</span>
      <JobCell n={n} job={job} />
      {/* An archived row is out of attribution, so it has no coverage to state. */}
      <span className={mapping?.mapped ? styles.mapped : styles.unmapped} data-testid="mapping-cell" title={mapping?.text}>
        {mapping ? mapping.text : '—'}
      </span>
    </div>
  )
}

// The hierarchy card (V4): the level path at the head, the tree, and the
// add-a-level buttons pinned at the foot.
function Navigator({
  nodes,
  site,
  focus,
  showArchived,
  hideL2,
  archivedSite,
  onFocus,
  onAdd,
}: {
  nodes: StructureNode[]
  site: StructureSite
  focus: string | null
  showArchived: boolean
  hideL2: boolean
  archivedSite: boolean
  onFocus: (id: string) => void
  onAdd: (start: 'l1' | 'l2') => void
}) {
  const count = (id: string) => activeRowsUnder(nodes, id).length
  const shown = (n: StructureNode) => n.active || showArchived
  const levelLabel = (n: StructureNode) => (n.level === 1 ? site.level1_label : site.level2_label)
  const item = (n: StructureNode, depth: number) => (
    <button
      key={n.id}
      type="button"
      className={focus === n.id ? styles.treeOn : n.active ? styles.tree : styles.treeOff}
      style={{ paddingLeft: `${0.625 + depth * 1}rem` }}
      aria-current={focus === n.id ? 'true' : undefined}
      onClick={() => onFocus(n.id)}
    >
      <span className={styles.treeKind}>{levelLabel(n)}</span>
      <span className={styles.treeName}>{n.name}</span>
      <span className={styles.treeCount}>{n.active ? count(n.id) : 'empty'}</span>
    </button>
  )
  const top = childrenOf(nodes, null).filter((n) => n.level === 1 && shown(n))
  const levels = hideL2 ? [site.level1_label, site.level3_label] : [site.level1_label, site.level2_label, site.level3_label]
  const why = archivedSite ? 'This site is archived and takes no new rows' : undefined
  return (
    <nav className={styles.navigator} aria-label="Structure">
      <div className={styles.navHead}>
        <div className={styles.navTitle}>Hierarchy</div>
        <div className={styles.navLevels}>{levels.join(' → ')}</div>
      </div>
      <div className={styles.navTree}>
        {top.map((l1) => (
          <React.Fragment key={l1.id}>
            {item(l1, 0)}
            {!hideL2 &&
              childrenOf(nodes, l1.id)
                .filter((n) => n.level === 2 && shown(n))
                .map((l2) => item(l2, 1))}
          </React.Fragment>
        ))}
        {!top.length && <div className={styles.treeEmpty}>No {site.level1_label.toLowerCase()} yet</div>}
      </div>
      <div className={styles.navFoot}>
        <button type="button" className={styles.navAdd} disabled={archivedSite} title={why} onClick={() => onAdd('l1')}>
          Add {lower(site.level1_label)}
        </button>
        {!hideL2 && (
          <button type="button" className={styles.navAdd} disabled={archivedSite || !top.length} title={why} onClick={() => onAdd('l2')}>
            Add {lower(site.level2_label)}
          </button>
        )}
      </div>
    </nav>
  )
}

// A confirmation with nothing to fill in, and so nothing unsaved: a layer
// (h 425-458) that Escape and the scrim close at once.
export function Modal({ title, wide, busy, onClose, children }: { title: string; wide?: boolean; busy?: boolean; onClose: () => void; children: React.ReactNode }) {
  const modalEl = useRef<HTMLDivElement>(null)
  const { requestClose } = useLayer(true, onClose, { el: modalEl, modal: true, busy })
  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={`${o.modal} ${wide ? o.wide : ''}`} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{title}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          {children}
        </div>
      </div>
    </div>
  )
}

// `pending` is what the submit button says once its write has been out a
// second ("Moving…"); Cancel is held while it is out.
export function Foot({
  busy,
  label,
  pending,
  blocked,
  danger,
  error,
  onClose,
  onSubmit,
}: {
  busy: boolean
  label: string
  pending: string
  blocked?: string
  danger?: boolean
  error?: string
  onClose: () => void
  onSubmit: () => void
}) {
  return (
    <>
      <div className={o.foot}>
        <button type="button" className={o.ghost} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        {blocked && <span className={o.footNote}>{blocked}</span>}
        <PendingButton type="button" className={danger ? o.dangerBtn : o.primary} busy={busy} pending={pending} disabled={!!blocked} onClick={onSubmit}>
          {label}
        </PendingButton>
      </div>
      <Failed text={error} />
    </>
  )
}

export const ErrorLine = ({ text }: { text: string }) =>
  text ? (
    <div className={pj.errorLine} role="alert">
      {text}
    </div>
  ) : null

const Emptied = ({ list }: { list: { id: string; path: string }[] }) =>
  list.length ? (
    <div className={styles.warnBox}>
      <strong>{list.length === 1 ? 'This will empty and leave the active structure:' : 'These will empty and leave the active structure:'}</strong> {list.map((e) => e.path).join(', ')}. Their records are kept for history.
    </div>
  ) : null

// The boundaries a Move or Archive removes, named with their areas before
// the write; the count is what the request confirms.
const BoundariesGoing = ({ list, byId, hideL2, why }: { list: StructureBoundary[]; byId: Map<string, StructureNode>; hideL2: boolean; why: string }) =>
  list.length ? (
    <div className={styles.warnBox} data-testid="boundaries-going">
      <strong>
        {why}: {plural(list.length, 'boundary', 'boundaries')} will be removed:
      </strong>{' '}
      {list.map((b) => `${shownPath(byId, byId.get(b.node_id), hideL2)} (${areaText(b.area_m2)})`).join(', ')}. The shapes stay on record.
    </div>
  ) : null

const whole = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : NaN)
interface DialogProps {
  user: any
  data: Structure
  onClose: () => void
  onDone: (message: string) => Promise<void> | void
}

// "Add many, already configured": a complete path every time (§7g, "Creation
// always collects a complete path").
function AddMany({ user, data, focus, start: startAt, onClose, onDone }: DialogProps & { focus: StructureNode | null; start?: 'rows' | 'l2' | 'l1' }) {
  const { site, nodes } = data
  const l1s = childrenOf(nodes, null).filter((n) => n.level === 1)
  const l2s = nodes.filter((n) => n.level === 2).sort((a, b) => pathOf(nodes, a).localeCompare(pathOf(nodes, b), undefined, { numeric: true }))
  const focusL2 = focus?.level === 2 ? focus : focus?.level === 3 ? nodes.find((n) => n.id === focus.parent_id) : null
  // "Add block" and "Add tunnel" open it on that choice; Add rows (or no
  // choice) starts where the focus suggests.
  const initial: 'rows' | 'l2' | 'l1' =
    startAt === 'l1' || (startAt === 'l2' && l1s.length)
      ? startAt
      : focusL2
        ? 'rows'
        : focus?.level === 1
          ? 'l2'
          : l2s.length
            ? 'rows'
            : 'l1'
  const [where, setWhere] = useState(initial)
  const [l2Id, setL2Id] = useState(focusL2?.id || l2s[0]?.id || '')
  const [l1Id, setL1Id] = useState((focus?.level === 1 ? focus.id : l1s[0]?.id) || '')
  const [l1Name, setL1Name] = useState('')
  const [l2Name, setL2Name] = useState('')
  const [prefix, setPrefix] = useState(site.level3_label)
  const [start, setStart] = useState('')
  const [quantity, setQuantity] = useState('10')
  const [variety, setVariety] = useState('')
  const [plants, setPlants] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel close it, asking first
  // once anything is filled in.
  const modalEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ where, l2Id, l1Id, l1Name, l2Name, prefix, start, quantity, variety, plants })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: modalEl, modal: true, focusFirst: true, busy })

  const siblings = where === 'rows' ? childrenOf(nodes, l2Id) : []
  const suggested = suggestedStart(siblings, prefix)
  const startN = start.trim() ? whole(start) : suggested
  const qty = whole(quantity)
  const names = generatedNames(prefix, startN, qty)
  const parentText =
    where === 'rows'
      ? shownPath(nodes, nodes.find((n) => n.id === l2Id), site.show_level2 === false)
      : where === 'l2'
        ? `${pathOf(nodes, nodes.find((n) => n.id === l1Id))} / ${l2Name.trim() || `new ${lower(site.level2_label)}`}`
        : `${l1Name.trim() || `new ${lower(site.level1_label)}`} / ${l2Name.trim() || `new ${lower(site.level2_label)}`}`

  const problems = [
    where === 'rows' && !l2Id ? `Choose the ${lower(site.level2_label)}` : '',
    where === 'l2' && !l1Id ? `Choose the ${lower(site.level1_label)}` : '',
    where === 'l1' && !l1Name.trim() ? `Name the new ${lower(site.level1_label)}` : '',
    where !== 'rows' && !l2Name.trim() ? `Name its first ${lower(site.level2_label)}` : '',
    !prefix.trim() ? 'Give the rows a name prefix' : '',
    !Number.isInteger(startN) ? 'The starting number is a whole number' : '',
    !(Number.isInteger(qty) && qty >= 1 && qty <= MAX_ADD) ? `Add between 1 and ${MAX_ADD} at a time` : '',
    !variety.trim() ? 'Choose the exact product and variety' : '',
    !Number.isInteger(whole(plants)) ? 'Plants each is a whole number' : '',
  ].filter(Boolean)

  const submit = async () => {
    if (busy || problems.length) return
    const body: Record<string, unknown> = { prefix: prefix.trim(), start: startN, quantity: qty, exact_variety: variety.trim(), plant_count: whole(plants) }
    if (where === 'rows') body.parent_id = l2Id
    if (where === 'l2') Object.assign(body, { parent_id: l1Id, level2_name: l2Name.trim() })
    if (where === 'l1') Object.assign(body, { level1_name: l1Name.trim(), level2_name: l2Name.trim() })
    setBusy(true)
    setError('')
    try {
      const r = await apiJson<{ created: StructureNode[] }>(user, `/api/sites/${site.id}/structure/rows`, { method: 'POST', body })
      const rows = r.created.filter((n) => n.level === 3)
      await onDone(`${plural(rows.length, lower(site.level3_label), `${lower(site.level3_label)}s`)} added under ${parentText}`)
    } catch (e: any) {
      setError(err(e, 'Nothing was added'))
    } finally {
      setBusy(false)
    }
  }

  const option = (k: typeof where, label: string, disabled = false) => (
    <label className={`${o.choice} ${styles.choice} ${where === k ? `${o.choiceOn} ${styles.choiceSel}` : ''} ${disabled ? styles.choiceOff : ''}`}>
      <input className={styles.hiddenRadio} type="radio" name="where" checked={where === k} disabled={disabled} onChange={() => setWhere(k)} />
      <span className={o.radio} aria-hidden="true" />
      <span>{label}</span>
    </label>
  )

  // The title and intro follow the choice (V4 "Add block" / "Add tunnel" /
  // "Add rows"), so the button pressed names the dialog it opens.
  const l1L = lower(site.level1_label)
  const l2L = lower(site.level2_label)
  const l3L = lower(site.level3_label)
  const title = where === 'l1' ? `Add ${l1L}` : where === 'l2' ? `Add ${l2L}` : `Add ${l3L}s`
  const intro =
    where === 'l1'
      ? `A ${l1L} is created with its first ${l2L} and ${l3L}s, so the path is complete before it saves.`
      : where === 'l2'
        ? `A ${l2L} is created with its first ${l3L}s: an empty container never enters the active structure.`
        : 'Configure them once here rather than creating unknown varieties and editing each one after.'

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={`${o.modal} ${o.wide}`} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{title}</h2>
            <p className={o.sub}>{intro}</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${styles.stack} ${inertClass(busy, slow)}`}>
            <div className={styles.radios} role="radiogroup" aria-label="Where">
              {option('rows', `${site.level3_label}s in an existing ${lower(site.level2_label)}`, !l2s.length)}
              {option('l2', `A new ${lower(site.level2_label)} in an existing ${lower(site.level1_label)}`, !l1s.length)}
              {option('l1', `A new ${lower(site.level1_label)}`)}
            </div>

            {where === 'rows' && (
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="am-l2">
                  {site.level2_label}
                </label>
                <select id="am-l2" className={o.input} value={l2Id} onChange={(e) => setL2Id(e.target.value)}>
                  {l2s.map((n) => (
                    <option key={n.id} value={n.id}>
                      {pathOf(nodes, n)}
                      {n.active ? '' : ' (empty)'}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {where === 'l2' && (
              <div className={o.grid2}>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="am-l1">
                    {site.level1_label}
                  </label>
                  <select id="am-l1" className={o.input} value={l1Id} onChange={(e) => setL1Id(e.target.value)}>
                    {l1s.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.name}
                        {n.active ? '' : ' (empty)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="am-l2name">
                    New {lower(site.level2_label)}
                  </label>
                  <input id="am-l2name" className={o.input} value={l2Name} onChange={(e) => setL2Name(e.target.value)} placeholder={`e.g. ${site.level2_label} 1`} />
                </div>
              </div>
            )}
            {where === 'l1' && (
              <div className={o.grid2}>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="am-l1name">
                    New {lower(site.level1_label)}
                  </label>
                  <input id="am-l1name" className={o.input} value={l1Name} onChange={(e) => setL1Name(e.target.value)} placeholder={`e.g. ${site.level1_label} A`} />
                </div>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="am-l2name">
                    Its first {lower(site.level2_label)}
                  </label>
                  <input id="am-l2name" className={o.input} value={l2Name} onChange={(e) => setL2Name(e.target.value)} placeholder={`e.g. ${site.level2_label} 1`} />
                </div>
              </div>
            )}

            <div className={styles.grid4}>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="am-prefix">
                  Prefix
                </label>
                <input id="am-prefix" className={o.input} value={prefix} onChange={(e) => setPrefix(e.target.value)} />
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="am-start">
                  Starting number
                </label>
                <input id="am-start" className={o.input} inputMode="numeric" value={start} placeholder={String(suggested)} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="am-qty">
                  Quantity
                </label>
                <input id="am-qty" className={o.input} inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="am-plants">
                  Plants each
                </label>
                <input id="am-plants" className={o.input} inputMode="numeric" value={plants} onChange={(e) => setPlants(e.target.value)} placeholder="0" />
              </div>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="am-variety">
                Exact product and variety
              </label>
              <VarietySelect id="am-variety" list={data.varieties} value={variety} onChange={setVariety} />
            </div>

            <div className={styles.preview} data-testid="add-preview">
              {names.length ? (
                <>
                  <div className={styles.previewTitle}>
                    {plural(names.length, lower(site.level3_label), `${lower(site.level3_label)}s`)} under {parentText}
                  </div>
                  <div>{previewLine(names)}</div>
                  <div className={styles.previewNote}>
                    Each is {variety.trim() || 'the chosen variety'}
                    {Number.isInteger(whole(plants)) ? ` with ${whole(plants).toLocaleString('en-NZ')} plants` : ''}, and gets its own Location ID.
                  </div>
                </>
              ) : (
                <div className={styles.previewNote}>The names appear here as you fill in the prefix, starting number and quantity.</div>
              )}
            </div>
          </div>
        </div>

        <Foot
          busy={busy}
          label={names.length ? `Create ${plural(names.length, lower(site.level3_label), `${lower(site.level3_label)}s`)}` : 'Create'}
          pending="Creating…"
          blocked={problems[0]}
          error={error}
          onClose={requestClose}
          onSubmit={submit}
        />
      </div>
      {confirm}
    </div>
  )
}

// "Bulk edit states what is mixed and blocks nothing silently."
function BulkEdit({ user, data, rows, onClose, onDone, onStale }: DialogProps & { rows: StructureNode[]; onStale: () => Promise<void> | void }) {
  const [variety, setVariety] = useState('')
  const [plants, setPlants] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel close it, asking first
  // once a field is changed.
  const modalEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ variety, plants })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: modalEl, modal: true, focusFirst: true, busy })
  const v = common(rows.map((r) => r.exact_variety || ''))
  const p = common(rows.map((r) => r.plant_count))
  const plan = variety.trim() ? varietyPlan(rows, data.jobs, variety) : null
  const plantN = plants.trim() ? whole(plants) : undefined
  const eligible = plan ? rows.length - plan.locked.length : rows.length
  const byId = new Map(data.nodes.map((n) => [n.id, n]))
  const hideL2 = data.site.show_level2 === false

  const blocked = !plan && plantN === undefined ? 'Change a field to apply' : plantN !== undefined && !Number.isInteger(plantN) ? 'Plants is a whole number' : plan && !eligible && plantN === undefined ? 'No eligible rows' : ''
  const label = plan
    ? plantN !== undefined && plan.locked.length
      ? `Apply: variety to ${eligible}, plants to ${rows.length}`
      : `Apply to ${plural(eligible, 'eligible row', 'eligible rows')}`
    : `Apply to ${plural(rows.length, 'row', 'rows')}`

  const submit = async () => {
    if (busy || blocked) return
    const body: Record<string, unknown> = { row_ids: rows.map((r) => r.id) }
    // Untouched fields are not sent.
    if (plan) Object.assign(body, { exact_variety: variety.trim(), acknowledged_locked: plan.locked.map((r) => r.id) })
    if (plantN !== undefined) body.plant_count = plantN
    setBusy(true)
    setError('')
    try {
      const r = await apiJson<{ variety_changed: string[]; plant_count_changed: string[]; left_unchanged_locked: unknown[]; unassigned: { name: string; job: { name: string } }[] }>(
        user,
        `/api/sites/${data.site.id}/structure/bulk-edit`,
        { method: 'POST', body }
      )
      const parts = []
      if (r.variety_changed.length) parts.push(`variety changed on ${plural(r.variety_changed.length, 'row', 'rows')}`)
      if (r.plant_count_changed.length) parts.push(`plants changed on ${plural(r.plant_count_changed.length, 'row', 'rows')}`)
      if (r.left_unchanged_locked.length) parts.push(`${r.left_unchanged_locked.length} kept their variety (picking recorded)`)
      if (r.unassigned.length) parts.push(`${plural(r.unassigned.length, 'row', 'rows')} unassigned from ${[...new Set(r.unassigned.map((u) => u.job.name))].join(', ')}`)
      const text = parts.join('; ')
      await onDone(text.charAt(0).toUpperCase() + text.slice(1))
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'ELIGIBILITY_CHANGED') {
        // "Recheck eligibility at save time": the panel now reads the
        // structure again and names the rows picking locks.
        setError(`${e.body.error} ${plural(e.body.locked?.length || 0, 'row is', 'rows are')} locked now.`)
        await onStale()
      } else {
        setError(err(e, 'Nothing was changed'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={o.modal} role="dialog" aria-label={`Edit ${plural(rows.length, 'row', 'rows')}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{`Edit ${plural(rows.length, 'row', 'rows')}`}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${styles.stack} ${inertClass(busy, slow)}`}>
            <div className={styles.prose}>Only the fields you change are applied. The rest stay as each row has them.</div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="be-variety">
                Exact product and variety
              </label>
              <VarietySelect id="be-variety" list={data.varieties} value={variety} onChange={setVariety} empty={`${v.mixed ? 'Multiple values' : v.value || 'No variety'}: leave as it is`} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="be-plants">
                Plants
              </label>
              <input id="be-plants" className={o.input} inputMode="numeric" value={plants} onChange={(e) => setPlants(e.target.value)} placeholder={p.mixed ? 'Multiple values' : p.value == null ? 'None' : String(p.value)} />
            </div>

            {plan && plan.locked.length > 0 && (
              <div className={o.warn} data-testid="locked-rows">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
                </svg>
                <span>
                  <strong>
                    Picking recorded on {plural(plan.locked.length, 'row', 'rows')}, so {plan.locked.length === 1 ? 'its' : 'their'} variety stays as it is:
                  </strong>{' '}
                  {plan.locked.map((r) => `${shownPath(byId, r, hideL2)} (${kg(r.picked_kg)})`).join(', ')}
                </span>
              </div>
            )}
            {plan && plan.unassigned.length > 0 && (
              <div className={o.warn} data-testid="unassigned-rows">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
                </svg>
                <span>
                  <strong>{plural(plan.unassigned.length, 'row', 'rows')} will be unassigned</strong>, because {plan.unassigned.length === 1 ? 'it' : 'they'} would no longer match {plan.unassigned.length === 1 ? 'its job' : 'their jobs'}:{' '}
                  {plan.unassigned.map((u) => `${shownPath(byId, u.row, hideL2)} from ${u.job.name}`).join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>

        <Foot busy={busy} label={label} pending="Applying…" blocked={blocked} error={error} onClose={requestClose} onSubmit={submit} />
      </div>
      {confirm}
    </div>
  )
}

// "Move preserves identity". Rows go to a level 2, a level 2 to a level 1,
// within the Site; a level 1 has no alternative parent.
function MoveDialog({
  user,
  data,
  ids,
  onClose,
  onDone,
  onStale,
  onReorder,
}: DialogProps & { ids: string[]; onStale: () => Promise<void> | void; onReorder: (n: StructureNode) => void }) {
  const { site, nodes } = data
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const moving = ids.map((id) => byId.get(id)).filter((n): n is StructureNode => !!n)
  const level = moving[0]?.level
  const targets = level === 3 || level === 2 ? nodes.filter((n) => n.level === level - 1).sort((a, b) => pathOf(byId, a).localeCompare(pathOf(byId, b), undefined, { numeric: true })) : []
  const [to, setTo] = useState(targets.find((t) => moving.some((m) => m.parent_id !== t.id))?.id || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel close it, asking first
  // once another destination is chosen.
  const modalEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ to })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: modalEl, modal: true, focusFirst: level !== 1, busy })
  const label = site[`level${level}_label` as 'level1_label'] || ''
  const noun = moving.length === 1 ? moving[0].name : plural(moving.length, lower(label), `${lower(label)}s`)

  if (level === 1) {
    return (
      <div className={o.scrim} onClick={requestClose}>
        <div ref={modalEl} className={o.modal} role="dialog" aria-label={`Move ${moving[0].name}`} onClick={(e) => e.stopPropagation()}>
          <div className={o.head}>
            <div className={o.headText}>
              <h2 className={o.title}>{`Move ${moving[0].name}`}</h2>
            </div>
            <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className={o.body}>
            <p className={styles.prose}>
              A {lower(site.level1_label)} has no other parent in a Site, so it cannot be moved. Changing where it sits in the list is reordering.
            </p>
          </div>
          <div className={o.foot}>
            <button type="button" className={o.ghost} onClick={requestClose}>
              Close
            </button>
            <button type="button" className={o.primary} onClick={() => onReorder(moving[0])}>
              Reorder {lower(site.level1_label)}s
            </button>
          </div>
        </div>
        {confirm}
      </div>
    )
  }

  const moveIds = new Set(moving.filter((m) => m.parent_id !== to).map((m) => m.id))
  const emptied = to ? emptiedBy(nodes, afterMove(nodes, moveIds, to)) : []
  const going = to ? boundariesOnMove(nodes, data.boundaries, moveIds, to) : []
  const target = byId.get(to)
  const blocked = !to ? `Choose a ${lower(level === 3 ? site.level2_label : site.level1_label)}` : !moveIds.size ? `Already in ${shownPath(byId, target, site.show_level2 === false)}` : ''
  // "At a coarser mapping level there are no child polygons to clear, and the
  // preview says the moved rows will use the destination's coverage."
  const coverage = target && level > site.map_level ? coverageOf(byId, site, data.boundaries, target) : null

  const submit = async () => {
    if (busy || blocked) return
    setBusy(true)
    setError('')
    try {
      await apiJson(user, `/api/sites/${site.id}/structure/move`, { method: 'POST', body: { node_ids: [...moveIds], parent_id: to, boundaries_removed: going.length } })
      await onDone(`${noun} moved to ${shownPath(byId, target, site.show_level2 === false)}${going.length ? `; ${plural(going.length, 'boundary', 'boundaries')} removed` : ''}`)
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'BOUNDARIES_WOULD_BE_REMOVED') {
        // The map changed after this opened: read it again so the count
        // below is the one the server holds, and let them confirm that.
        setError(e.body.error)
        await onStale()
      } else {
        setError(err(e, 'Nothing was moved'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={o.modal} role="dialog" aria-label={`Move ${noun}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{`Move ${noun}`}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${styles.stack} ${inertClass(busy, slow)}`}>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="mv-to">
                To {lower(level === 3 ? site.level2_label : site.level1_label)}
              </label>
              <select id="mv-to" className={o.input} value={to} onChange={(e) => setTo(e.target.value)}>
                <option value="">Choose…</option>
                {targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {pathOf(byId, t)}
                    {t.active ? '' : ' (empty)'}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.prose}>
              Within {site.name} only. Names, Location IDs, picking jobs and recorded picking move unchanged.
              {coverage?.at
                ? ` ${site.name} is mapped by ${lower(site[`level${site.map_level}_label` as 'level1_label'])}, so a ${lower(label)} has no boundary of its own to clear: the moved ${lower(site.level3_label)}s use ${coverage.at.name}'s coverage, which is ${coverage.mapped ? 'mapped' : 'unmapped'}.`
                : ''}
            </div>
            {moveIds.size > 0 && moveIds.size < moving.length && <div className={styles.prose}>{plural(moving.length - moveIds.size, 'is', 'are')} already there and stay put.</div>}
            <Emptied list={emptied} />
            <BoundariesGoing list={going} byId={byId} hideL2={site.show_level2 === false} why="Moving clears geometry" />
          </div>
        </div>
        <Foot busy={busy} label="Move" pending="Moving…" blocked={blocked} error={error} onClose={requestClose} onSubmit={submit} />
      </div>
      {confirm}
    </div>
  )
}

// "Only final rows are archived directly." A branch archive is every active
// row in it; the Site keeps at least one.
function ArchiveDialog({
  user,
  data,
  rowIds,
  nodeId,
  onClose,
  onDone,
  onReplant,
  onStale,
}: DialogProps & { rowIds?: string[]; nodeId?: string; onReplant: (id: string) => void; onStale: () => Promise<void> | void }) {
  const { site, nodes } = data
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const branch = nodeId ? byId.get(nodeId) : undefined
  const rows = branch ? activeRowsUnder(nodes, branch.id) : (rowIds || []).map((id) => byId.get(id)).filter((n): n is StructureNode => !!n && isActiveRow(n))
  const ids = new Set(rows.map((r) => r.id))
  const last = rows.length > 0 && leavesNoActiveRow(nodes, ids)
  const emptied = emptiedBy(nodes, afterArchive(nodes, ids))
  const going = boundariesOnArchive(nodes, data.boundaries, ids)
  const picked = rows.filter((r) => r.picking_recorded)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape and the scrim close it.
  const modalEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el: modalEl, modal: true, busy })
  const noun = plural(rows.length, lower(site.level3_label), `${lower(site.level3_label)}s`)

  const submit = async () => {
    if (busy || last || !rows.length) return
    setBusy(true)
    setError('')
    try {
      const scope = branch ? { node_id: branch.id } : { row_ids: [...ids] }
      await apiJson(user, `/api/sites/${site.id}/structure/archive`, { method: 'POST', body: { ...scope, boundaries_removed: going.length } })
      await onDone(`${noun} archived${going.length ? `; ${plural(going.length, 'boundary', 'boundaries')} removed` : ''}`)
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'BOUNDARIES_WOULD_BE_REMOVED') {
        setError(e.body.error)
        await onStale()
      } else {
        setError(err(e, 'Nothing was archived'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={o.modal} role="dialog" aria-label={branch ? `Archive every ${lower(site.level3_label)} in ${branch.name}` : `Archive ${noun}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{branch ? `Archive every ${lower(site.level3_label)} in ${branch.name}` : `Archive ${noun}`}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${styles.stack} ${inertClass(busy, slow)}`}>
            <div className={styles.paths}>{previewLine(rows.map((r) => shownPath(byId, r, site.show_level2 === false)))}</div>
            <div className={styles.prose}>
              Archived rows leave the active structure and take no new picking. Their recorded picking{picked.length ? ` (${plural(picked.length, 'row', 'rows')})` : ''} and picking-job links stay with them.
            </div>
            <Emptied list={emptied} />
            <BoundariesGoing list={going} byId={byId} hideL2={site.show_level2 === false} why="Archiving takes them out of attribution" />
            {last && (
              <div className={o.danger} role="alert">
                <p>
                  A Site must keep at least one active {lower(site.level3_label)}.
                  {rows.length === 1 ? ' Use Replant to replace the last one.' : ' Use Replant to replace the last one, or archive fewer.'}
                </p>
                {rows.length === 1 && (
                  <button type="button" className={styles.linkBtn} onClick={() => onReplant(rows[0].id)}>
                    Replant {rows[0].name}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <Foot
          busy={busy}
          label={`Archive ${noun}`}
          pending="Archiving…"
          danger
          blocked={last ? 'The Site would have no active row' : !rows.length ? 'Nothing to archive' : ''}
          error={error}
          onClose={requestClose}
          onSubmit={submit}
        />
      </div>
      {confirm}
    </div>
  )
}

// "Replant / replace is one atomic operation." The successor keeps the
// Location ID by default (§7g's adopted default).
function ReplantDialog({ user, data, row, onClose, onDone }: DialogProps & { row: StructureNode }) {
  const { site, nodes, jobs } = data
  const [variety, setVariety] = useState('')
  const [plants, setPlants] = useState(row.plant_count == null ? '' : String(row.plant_count))
  const [locId, setLocId] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  // A layer (h 425-458): Escape, the scrim and Cancel close it, asking first
  // once a field is changed.
  const modalEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ variety, plants, locId })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: modalEl, modal: true, focusFirst: true, busy })
  const job = jobs.find((j) => j.id === row.picking_job_id)
  const blocked = !variety.trim() ? 'Choose the new exact product and variety' : !Number.isInteger(whole(plants)) ? 'Plants is a whole number' : ''

  const submit = async () => {
    if (busy || blocked) return
    setBusy(true)
    setError('')
    try {
      // With no Location ID to keep, the box asks for a new one instead.
      const keep = row.location_id ? locId : !locId
      const r = await apiJson<{ successor: StructureNode }>(user, `/api/structure/${row.id}/replant`, {
        method: 'POST',
        body: { exact_variety: variety.trim(), plant_count: whole(plants), keep_location_id: keep },
      })
      await onDone(`${row.name} replanted as ${r.successor.exact_variety}${r.successor.location_id ? ` (${r.successor.location_id})` : ''}. It is unassigned.`)
    } catch (e: any) {
      setError(err(e, 'Nothing was replanted'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={o.modal} role="dialog" aria-label={`Replant ${row.name}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{`Replant ${row.name}`}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${styles.stack} ${inertClass(busy, slow)}`}>
            <div className={styles.prose}>
              {shownPath(nodes, row, data.site.show_level2 === false)} is {row.exact_variety || 'unplanted'}. Replanting archives this planting and creates a linked one in its place, in one step.
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="rp-variety">
                New exact product and variety
              </label>
              <VarietySelect id="rp-variety" list={data.varieties} value={variety} onChange={setVariety} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="rp-plants">
                Plants
              </label>
              <input id="rp-plants" className={o.input} inputMode="numeric" value={plants} onChange={(e) => setPlants(e.target.value)} />
            </div>
            <div className={o.toggleRow}>
              <div className={o.toggleText}>
                <div className={o.toggleTitle} id="rp-locid">
                  {row.location_id ? `Keep Location ID ${row.location_id}: same ground, new planting` : 'Give the new planting a Location ID'}
                </div>
              </div>
              <button type="button" role="switch" aria-checked={locId} aria-labelledby="rp-locid" className={o.switch} onClick={() => setLocId(!locId)} />
            </div>
            <div className={styles.prose}>
              The old planting keeps its recorded picking{job ? ` and its job, ${job.name}` : ''}. The new one starts unassigned; add it to a job of its variety from Picking jobs.
              {data.boundaries.some((b) => b.node_id === row.id) ? ' Its boundary goes to the new planting: same ground.' : ''}
            </div>
          </div>
        </div>
        <Foot busy={busy} label="Replant" pending="Replanting…" blocked={blocked} error={error} onClose={requestClose} onSubmit={submit} />
      </div>
      {confirm}
    </div>
  )
}

function RenameDialog({ user, data, node, onClose, onDone }: DialogProps & { node: StructureNode }) {
  const [name, setName] = useState(node.name)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  const label = data.site[`level${node.level}_label` as 'level1_label']
  const blocked = !name.trim() ? 'Name is required' : name.trim() === node.name ? 'Nothing has changed' : ''
  // A layer (h 425-458): Escape, the scrim and Cancel close it, asking first
  // once the name is changed.
  const modalEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: name !== node.name && !busy, el: modalEl, modal: true, focusFirst: true, busy })
  const submit = async () => {
    if (busy || blocked) return
    setBusy(true)
    setError('')
    try {
      await apiJson(user, `/api/structure/${node.id}`, { method: 'PATCH', body: { name: name.trim() } })
      await onDone(`${node.name} renamed ${name.trim()}`)
    } catch (e: any) {
      setError(err(e, 'The name was not changed'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={o.modal} role="dialog" aria-label={`Rename ${lower(label)}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{`Rename ${lower(label)}`}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${styles.stack} ${inertClass(busy, slow)}`}>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="rn-name">
                Name
              </label>
              <input id="rn-name" className={o.input} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className={styles.prose}>Renaming keeps every row, Location ID and job as it is.</div>
          </div>
        </div>
        <Foot busy={busy} label="Rename" pending="Renaming…" blocked={blocked} error={error} onClose={requestClose} onSubmit={submit} />
      </div>
      {confirm}
    </div>
  )
}

// "Level 1 / 2 own name, parent, ordering" (§7g). Every sibling is listed,
// empty ones too, and moved a place at a time; the save sends the whole list,
// so one added or moved since this opened is refused and the list redrawn.
// Rows are listed by name and are not reordered here.
function ReorderDialog({
  user,
  data,
  parentId,
  focusId,
  onClose,
  onDone,
  onStale,
}: DialogProps & { parentId: string | null; focusId?: string; onStale: () => Promise<void> | void }) {
  const { site, nodes } = data
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const siblings = childrenOf(nodes, parentId)
  const key = siblings.map((n) => n.id).join()
  const [order, setOrder] = useState(siblings.map((n) => n.id))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  // Redrawn from what the server holds after a refusal.
  useEffect(() => setOrder(key ? key.split(',') : []), [key])
  const parent = parentId ? byId.get(parentId) : undefined
  const level = parent ? 2 : 1
  const label = lower(site[`level${level}_label` as 'level1_label'])
  const where = parent ? `in ${shownPath(byId, parent, site.show_level2 === false)}` : `in ${site.name}`
  const changed = order.some((id, i) => siblings[i]?.id !== id)
  // A layer (h 425-458): Escape, the scrim and Cancel close it, asking first
  // once the order is changed.
  const modalEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: changed && !busy, el: modalEl, modal: true, busy })
  const blocked = siblings.length < 2 ? `There is only one ${label} ${where}` : !changed ? 'Nothing has changed' : ''
  const shift = (i: number, by: number) =>
    setOrder((o) => {
      const next = [...o]
      ;[next[i], next[i + by]] = [next[i + by], next[i]]
      return next
    })

  const submit = async () => {
    if (busy || blocked) return
    setBusy(true)
    setError('')
    try {
      await apiJson(user, `/api/sites/${site.id}/structure/reorder`, { method: 'POST', body: { parent_id: parentId, order } })
      await onDone(`${label[0].toUpperCase()}${label.slice(1)}s ${where} reordered`)
    } catch (e: any) {
      setError(err(e, 'The order was not changed'))
      if (e instanceof ApiError && e.body?.code === 'ORDER_CHANGED') await onStale()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={modalEl} className={o.modal} role="dialog" aria-label={`Reorder ${label}s ${where}`} onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{`Reorder ${label}s ${where}`}</h2>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${styles.stack} ${inertClass(busy, slow)}`}>
            <ol className={styles.orderList} aria-label={`${label}s in order`}>
              {order.map((id, i) => {
                const n = byId.get(id)
                if (!n) return null
                return (
                  <li key={id} className={id === focusId ? `${styles.orderItem} ${styles.orderItemOn}` : styles.orderItem}>
                    <span className={styles.orderPos}>{i + 1}</span>
                    <span className={styles.orderName}>{n.name}</span>
                    {!n.active && <span className={styles.orderEmpty}>empty</span>}
                    <button type="button" className={styles.orderBtn} aria-label={`Move ${n.name} up`} disabled={i === 0} onClick={() => shift(i, -1)}>
                      ↑
                    </button>
                    <button type="button" className={styles.orderBtn} aria-label={`Move ${n.name} down`} disabled={i === order.length - 1} onClick={() => shift(i, 1)}>
                      ↓
                    </button>
                  </li>
                )
              })}
            </ol>
            <div className={styles.prose}>
              Order only: nothing is moved to another parent, and every {lower(site.level3_label)}, Location ID, job and boundary stays as it is.
            </div>
          </div>
        </div>
        <Foot busy={busy} label="Save order" pending="Saving…" blocked={blocked} error={error} onClose={requestClose} onSubmit={submit} />
      </div>
      {confirm}
    </div>
  )
}

// The row inspector slide-over. "Only variety is locked by picking history."
function Inspector({
  user,
  row,
  nodes,
  site,
  jobs,
  varieties,
  onClose,
  onSaved,
  onReplant,
  onArchive,
}: {
  user: any
  row: StructureNode
  nodes: StructureNode[]
  site: StructureSite
  jobs: StructureJob[]
  varieties: SiteVariety[]
  onClose: () => void
  onSaved: (message: string) => void
  onReplant: () => void
  onArchive: () => void
}) {
  const [name, setName] = useState(row.name)
  const [variety, setVariety] = useState(row.exact_variety || '')
  const [plants, setPlants] = useState(row.plant_count == null ? '' : String(row.plant_count))
  const [locId, setLocId] = useState(row.location_id || '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const slow = useSlow(busy)
  const archived = !!row.archived_at
  const job = jobs.find((j) => j.id === row.picking_job_id) || null
  const predecessor = row.predecessor_id ? nodes.find((n) => n.id === row.predecessor_id) : undefined
  const successor = nodes.find((n) => n.predecessor_id === row.id)

  const body: Record<string, unknown> = {}
  if (name.trim() !== row.name) body.name = name.trim()
  if (!row.picking_recorded && variety.trim() !== (row.exact_variety || '')) body.exact_variety = variety.trim()
  if (plants.trim() !== (row.plant_count == null ? '' : String(row.plant_count))) body.plant_count = whole(plants)
  if (locId.trim() !== (row.location_id || '')) body.location_id = locId.trim()
  const changed = Object.keys(body).length > 0
  // A layer (h 425-458): Escape, the scrim and × close it; unsaved changes
  // ask first.
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: !archived && changed && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  const leaves = job && 'exact_variety' in body && job.exact_variety !== body.exact_variety
  const blocked =
    'name' in body && !body.name
      ? 'Name is required'
      : 'exact_variety' in body && !body.exact_variety
        ? 'Choose the exact product and variety'
        : 'plant_count' in body && !Number.isInteger(body.plant_count)
          ? 'Plants is a whole number'
          : 'location_id' in body && !body.location_id
            ? 'Location ID is required'
            : ''

  const save = async () => {
    if (busy || blocked || !changed) return
    setBusy(true)
    setError('')
    try {
      const r = await apiJson<{ node: StructureNode; unassigned_from: { name: string } | null }>(user, `/api/structure/${row.id}`, { method: 'PATCH', body })
      onSaved(`${r.node.name} saved${r.unassigned_from ? `. It no longer matches ${r.unassigned_from.name} and was unassigned.` : ''}`)
    } catch (e: any) {
      setError(err(e, 'The row was not saved'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.drawerScrim} onClick={requestClose}>
      <div ref={panelEl} className={o.drawer} role="dialog" aria-label={row.name} onClick={(e) => e.stopPropagation()}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>{row.name}</h2>
            <p className={o.drawerSub}>
              {site.level3_label} · {parentPath(nodes, row)}
              {archived ? ' · Archived' : ''}
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={`${o.drawerBody} ${inertClass(busy, slow)}`}>
          {archived && (
            <div className={o.warn}>
              <span>
                Archived {new Date(row.archived_at!).toLocaleDateString('en-NZ')}. An archived row is kept for its history and is read-only.
                {successor ? ` Replanted as ${successor.exact_variety || successor.name}.` : ''}
              </span>
            </div>
          )}
          {predecessor && (
            <p className={styles.prose}>
              Replants the earlier {predecessor.exact_variety || 'planting'} here, archived {predecessor.archived_at ? new Date(predecessor.archived_at).toLocaleDateString('en-NZ') : ''}.
            </p>
          )}

          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="ri-name">
              Name
            </label>
            <input id="ri-name" className={o.input} value={name} disabled={archived} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className={o.field}>
            <label className={o.fieldLabel} htmlFor="ri-variety">
              Exact product and variety
            </label>
            <VarietySelect id="ri-variety" list={varieties} value={variety} current={row.exact_variety} disabled={archived || !!row.picking_recorded} onChange={setVariety} />
            {row.picking_recorded && (
              <span className={styles.lockNote}>
                <Padlock /> Variety locked: {kg(row.picked_kg)} of picking is recorded against this row. Replant it to change what is planted.
              </span>
            )}
            {leaves && <span className={styles.warnNote}>This row will be unassigned from {job!.name}, which picks {job!.exact_variety}.</span>}
          </div>

          <div className={o.field}>
            <div className={o.grid2}>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="ri-plants">
                  Plants
                </label>
                <input id="ri-plants" className={o.input} inputMode="numeric" value={plants} disabled={archived} onChange={(e) => setPlants(e.target.value)} />
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="ri-loc">
                  Location ID
                </label>
                <input id="ri-loc" className={o.input} value={locId} disabled={archived} onChange={(e) => setLocId(e.target.value)} placeholder="None" />
              </div>
            </div>
            <span className={o.fieldHint}>A Location ID is unique among the workspace's active rows. Moving or renaming never changes it.</span>
          </div>
          <PinStrip user={user} node={row} nodes={nodes} site={site} onSaved={onSaved} />

          <div className={o.section}>
            <div className={o.label}>Picking job</div>
            <JobCell n={row} job={job} />
            <span className={o.fieldHint}>
              {row.picking_recorded && job
                ? 'Picking is recorded, so this row stays in its job, even if the job is made inactive or archived.'
                : 'Membership is changed from the job, in Picking jobs.'}{' '}
              <Link to={`/picking-jobs?site=${site.id}`}>Open Picking jobs</Link>
            </span>
          </div>
        </div>

        {!archived && (
          <div className={o.drawerFoot}>
            <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
              Cancel
            </button>
            <button type="button" className={o.ghost} onClick={onReplant} disabled={busy || !!site.archived_at}>
              Replant
            </button>
            <button type="button" className={o.ghost} disabled={busy} onClick={onArchive}>
              Archive
            </button>
            {blocked && <span className={o.footNote}>{blocked}</span>}
            <PendingButton type="button" className={o.primary} busy={busy} pending="Saving…" disabled={busy || !!blocked || !changed} onClick={save}>
              Save changes
            </PendingButton>
            <Failed text={error} />
          </div>
        )}
      </div>
      {confirm}
    </div>
  )
}

// The grower PIN a block, tunnel or row works under (§7g). Only a block may
// carry its own, which flows down to everything inside it; levels 2 and 3
// read theirs and offer no override. The PIN is saved on Done, not on every
// key; Escape leaves it as it was.
function PinStrip({ user, node, nodes, site, onSaved }: { user: any; node: StructureNode; nodes: StructureNode[]; site: StructureSite; onSaved: (message: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const [error, setError] = useState('')
  // The edit is a layer of its own (h 425-458): Escape ends it unsaved, and a
  // second press closes whatever it sits in.
  useLayer(editing, () => {
    setEditing(false)
    setError('')
  })
  const src = pinOf(nodes, site, node)
  const [l1, l2, l3] = [site.level1_label, site.level2_label, site.level3_label].map(lower)
  const own = node.level === 1 ? node.grower_pin : null
  const from = src.from === 'block' ? src.block!.name : site.name
  const long =
    src.from === 'own'
      ? `This ${l1} uses ${src.pin}, and so does everything inside it.`
      : node.level === 1
        ? `${src.pin ? `Inheriting ${src.pin} from the site.` : 'The site has no grower PIN.'} Type a PIN to give this ${l1} its own. It flows down to every ${l2} and ${l3} inside it.`
        : `${src.pin ? `Grower PIN ${src.pin}, inherited from ${from}.` : `No grower PIN is set on ${from}.`} Only a ${l1} can carry its own.`
  const short = src.from === 'own' ? `this ${l1}` : node.level === 1 ? 'inherited from site' : `inherited from ${from}`

  const save = async (pin: string | null, key: string) => {
    if (busy) return
    if (pin === own) {
      setEditing(false)
      return
    }
    setBusyOn(key)
    setError('')
    try {
      await apiJson(user, `/api/structure/${node.id}`, { method: 'PATCH', body: { grower_pin: pin } })
      setEditing(false)
      onSaved(pin ? `${node.name} now uses grower PIN ${pin}.` : `${node.name} inherits ${site.grower_pin ? `PIN ${site.grower_pin}` : 'the PIN'} from ${site.name} again.`)
    } catch (e: any) {
      setError(err(e, 'The PIN was not changed'))
    } finally {
      setBusyOn(null)
    }
  }

  return (
    <div className={sm.pin} data-testid="pin-strip">
      <span className={sm.pinLabel}>Grower PIN</span>
      {editing ? (
        <>
          <input
            className={sm.pinInput}
            aria-label={`Grower PIN for ${node.name}`}
            inputMode="numeric"
            autoFocus
            value={value}
            placeholder={`Inherits ${site.grower_pin || 'no PIN'} from ${site.name}`}
            onChange={(e) => setValue(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save(value.trim() || null, 'save')
            }}
            disabled={busy}
          />
          <PendingButton type="button" className={sm.pinBtn} busy={busyOn === 'save'} pending="Saving…" disabled={busy} onClick={() => save(value.trim() || null, 'save')}>
            Done
          </PendingButton>
          {own && (
            <PendingButton type="button" className={sm.pinBtn} busy={busyOn === 'inherit'} pending="Saving…" disabled={busy} title="Go back to inheriting the site's PIN" onClick={() => save(null, 'inherit')}>
              Use site PIN
            </PendingButton>
          )}
        </>
      ) : (
        <>
          <span className={src.pin ? sm.pinValue : sm.pinNone}>{src.pin || 'Not set'}</span>
          {src.pin && <span className={sm.pinFrom}>{short}</span>}
          {node.level === 1 && (
            <button
              type="button"
              className={sm.pinBtn}
              onClick={() => {
                setValue(own || '')
                setEditing(true)
              }}
            >
              {own ? 'Change' : 'Override'}
            </button>
          )}
        </>
      )}
      <span className={sm.pinHelp} title={long} aria-label={long} role="img">
        ?
      </span>
      {error && (
        <span className={pj.blocked} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

// "Surfaced as a History slide-over per Site": structural edits, settings,
// and the jobs, packing and rates on the Site, newest first, with who.
function HistoryPanel({ user, data, onClose }: { user: any; data: Structure; onClose: () => void }) {
  const [events, setEvents] = useState<HistoryEvent[] | null>(null)
  const [error, setError] = useState('')
  // A layer (h 425-458): Escape and × close it.
  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el: panelEl, modal: true })
  useEffect(() => {
    let live = true
    apiJson<HistoryEvent[]>(user, `/api/sites/${data.site.id}/structure/history`)
      .then((e) => live && setEvents(e))
      .catch((e) => live && setError(err(e, 'Could not load the history')))
    return () => {
      live = false
    }
  }, [user, data.site.id])

  return (
    <div className={o.drawerScrim} onClick={requestClose}>
      <div ref={panelEl} className={o.drawer} role="dialog" aria-label="Structure history" onClick={(e) => e.stopPropagation()}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h3 className={o.drawerTitle}>History</h3>
            <p className={o.drawerSub}>{data.site.name}: settings, structure, and its picking jobs, packing and rates</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.drawerBody}>
          <ErrorLine text={error} />
          {!events && !error && <div className={styles.prose}>Loading…</div>}
          {events && !events.length && <div className={styles.prose}>Nothing has been changed in this Site's structure yet.</div>}
          {events?.map((e) => {
            const h = historyEntry(e, data.jobs)
            const who = historyActor(e)
            return (
              <div key={e.id} className={styles.event} data-testid="history-event">
                <div className={styles.eventHead}>
                  <span className={styles.eventTitle}>{h.title}</span>
                  <span className={styles.eventTime}>{new Date(e.created_at).toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                </div>
                <div className={styles.eventBy}>{who ? `by ${who}` : 'Who: not recorded'}</div>
                {h.lines.filter(Boolean).map((l, i) => (
                  <div key={i} className={styles.eventLine}>
                    {l}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
      {confirm}
    </div>
  )
}
